import type { DiscoverPayload } from '@blackblack/shared';
import { formatCount } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useQuery } from '../../lib/query';
import { Link, navigate } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { Icon } from '../../components/ui/Icon';
import { ErrorState, Skeleton } from '../../components/ui/primitives';
import { Page, Section, Shelf } from '../../components/layout';
import { CreatorCard } from '../../components/UserRow';
import { TrackCard } from '../music/TrackCard';
import { TrackRow } from '../music/TrackRow';
import styles from './discover.module.css';

/** Ontdekken: search entry, trending hashtags, videos, music and creators. */
export function DiscoverPage() {
  const { me, status } = useAuth();
  const q = useQuery<DiscoverPayload>(status === 'loading' ? null : `discover:${me?.id ?? 'anon'}`, () => api.get('/discover'), { staleMs: 60_000 });
  const d = q.data;

  return (
    <Page>
      <header className={styles.header}>
        <h1>Ontdekken</h1>
        <button className={styles.search} onClick={() => navigate('/search')}>
          <Icon name="search" size={18} />
          <span>Zoek video's, muziek, mensen…</span>
        </button>
      </header>

      {q.error && <ErrorState error={q.error} onRetry={q.refetch} />}

      <div className={styles.tags}>
        {(d?.hashtags ?? Array.from({ length: 6 }, () => null)).map((h, i) =>
          h ? (
            <Link key={h.tag} to={`/tag/${h.tag}`} className={styles.tag}>
              <span className={styles.hash}>#</span>
              {h.tag}
              <small>{formatCount(h.videoCount)}</small>
            </Link>
          ) : (
            <Skeleton key={i} w={96} h={36} r={18} />
          ),
        )}
      </div>

      <Section title="Trending video's">
        <div className={styles.trendGrid}>
          {(d?.trendingVideos ?? Array.from({ length: 6 }, () => null)).slice(0, 6).map((v, i) =>
            v ? (
              <button key={v.id} className={styles.trendTile} onClick={() => navigate(`/v/${v.id}`, { state: { videos: d!.trendingVideos, index: i } })}>
                {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" loading="lazy" />}
                <span className={styles.rank}>{i + 1}</span>
                <span className={styles.trendMeta}>
                  <strong>@{v.author.username}</strong>
                  <span>
                    <Icon name="heart" size={12} filled /> {formatCount(v.stats.likes)}
                  </span>
                </span>
              </button>
            ) : (
              <Skeleton key={i} h="auto" r={12} className={styles.trendTile} />
            ),
          )}
        </div>
      </Section>

      <Section title="Trending muziek" to="/music">
        <div className={styles.rows}>{d?.trendingTracks.slice(0, 5).map((t, i) => <TrackRow key={t.id} track={t} list={d.trendingTracks} index={i} number={i + 1} context="Trending muziek" />)}</div>
      </Section>

      <Section title="Populaire creators">
        <Shelf>
          {d ? d.popularCreators.map((c) => <CreatorCard key={c.id} user={c} followerCount={c.followerCount} />) : [0, 1, 2].map((i) => <Skeleton key={i} w={140} h={190} r={20} />)}
        </Shelf>
      </Section>

      <Section title="Nieuwe muziek">
        <Shelf>{d?.newTracks.map((t, i) => <TrackCard key={t.id} track={t} list={d.newTracks} index={i} context="Nieuwe muziek" />)}</Shelf>
      </Section>
    </Page>
  );
}
